import {
  AfterViewChecked,
  ChangeDetectorRef,
  Component,
  DoCheck,
  EventEmitter,
  Input,
  IterableDiffers,
  inject,
  OnInit,
  OnChanges,
  Output,
  SimpleChanges,
  ChangeDetectionStrategy,
  ViewChild,
} from '@angular/core';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { FormControl } from '@angular/forms';
import { MenuItemDefinition } from '../../../model/menu-item-definition';
import { CommandBase, MultiselectCommand } from '../../../model/command';
import { CommandDispatcher } from '../../../service/command/command-dispatcher.service';
import { CommandFactoryService } from '../../../service/factory/command-factory.service';
import { ListViewColumnFactory } from '../../../service/factory/list-view-column-factory.service';
import { MessageService } from '../../../service/message.service';
import { ListViewBase } from '../list-view.base';
import { DefaultPageSize, PagingEvent } from '../paging/paging.component';
import { LanguageService } from '../../../service/language.service';
import { NavigationService } from '../../../service/navigation.service';
import { UserPreferencesService } from '../../../service/user-preferences.service';
import { getListPreferencesId } from '../../../helper/list-preferences-key';
import { Subscription } from 'rxjs';

@Component({
    selector: 'app-multiselect-list-view',
    templateUrl: './multiselect-list-view.component.html',
    styleUrls: ['./multiselect-list-view.component.scss', './../list-view/list-view.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class MultiselectListViewComponent<TSource = any> extends ListViewBase<TSource> implements DoCheck, OnChanges, OnInit, AfterViewChecked {
  constructor(
    private readonly _commandFactory: CommandFactoryService<any, TSource>,
    private readonly _messageService: MessageService,
    private readonly _commandDispatcher: CommandDispatcher,
    private readonly _cdr: ChangeDetectorRef,
    columnFactory: ListViewColumnFactory,
    languageService: LanguageService,
    iterableDiffers: IterableDiffers
  ) {
    super(columnFactory, languageService, iterableDiffers);

    this.control = new FormControl();
    this.control.valueChanges.subscribe(value => {
      const selectableRows = this.getSelectableRows();
      if (!value && !selectableRows.every(item => item.selected)) {
        return;
      }
      selectableRows.forEach(i => (i.selected = value));
      if (value && selectableRows.length === 0) this.control.setValue(false, { emitEvent: false });
      this.emitSelectedItemsWhenChanged(this.getSelectedItems());
    });
  }

  @Input() menuDefinition: MenuItemDefinition[];
  @Input() externalMenuCommandIds: string[] = [];
  @Input() selectAll: boolean;
  @Input() isToolbarSticky: boolean = false;
  @Input() preferencesKey: string;
  @Input() rememberPagination = false;
  @Output() menuCommand = new EventEmitter<CommandBase>();
  @Output() selectedItemsChange = new EventEmitter<TSource[]>();

  getRowNumber(index: number): number {
    return this.from + index + 1;
  }

  control: FormControl;
  isActive: boolean;
  selectedItemsCount: number;
  showMenu: boolean;
  get hasMenu(): boolean {
    return this.menuDefinition && this.menuDefinition.length > 0;
  }
  from: number = 0;
  to: number = DefaultPageSize;
  pageSize = DefaultPageSize;
  private preferencesId?: number;
  private readonly paginationSubscriptions = new Subscription();
  private readonly navigation = inject(NavigationService);
  private readonly userPreferences = inject(UserPreferencesService);
  private previouslySelectedItems: TSource[] = [];
  private selectionReconciliationScheduled = false;
  @ViewChild(CdkVirtualScrollViewport) private mobileViewport?: CdkVirtualScrollViewport;
  private recentIdScrolledInViewport?: string;

  get largeNumberOfItems(): boolean {
    if (this.itemsSource) return this.itemsSource.length > 100;
    else return false;
  }

  get smallNumberOfItems(): boolean {
    return this.itemsSource?.length <= 100;
  }

  getSelectedItems(): TSource[] {
    if (this.rows) return this.rows.filter(i => i.selected).map(x => x.dataSource);
    return [];
  }

  protected resetRows(): void {
    super.resetRows();
    this.scheduleSelectedItemsReconciliation();
    if (this.rememberPagination) this.clampPage();
  }

  ngOnInit(): void {
    if (!this.rememberPagination) return;

    const requestedPage = Number(this.navigation.getParameterValue('page'));
    const page = Number.isSafeInteger(requestedPage) && requestedPage > 1 && requestedPage <= 1000000 ? requestedPage : 1;
    if (!this.preferencesKey) {
      this.showPage(page);
      return;
    }

    this.preferencesId = getListPreferencesId(this.preferencesKey, this.navigation.url);
    this.paginationSubscriptions.add(this.userPreferences.getListPreferences(this.preferencesId).subscribe({
      next: preferences => {
        if (Number.isInteger(preferences.pageSize) && preferences.pageSize > 0) this.pageSize = preferences.pageSize;
        this.showPage(page);
      },
      error: () => this.showPage(page),
    }));
  }

  ngAfterViewChecked(): void {
    if (!this.largeNumberOfItems || !this.recentId || this.recentIdScrolledInViewport === this.recentId) return;
    const viewport = this.mobileViewport;
    if (!viewport?.elementRef.nativeElement.getClientRects().length) return;

    const index = this.rows.findIndex(row => row.highlight);
    if (index < 0) return;
    viewport.scrollToIndex(index);
    this.recentIdScrolledInViewport = this.recentId;
  }

  override ngOnDestroy(): void {
    this.paginationSubscriptions.unsubscribe();
    super.ngOnDestroy();
  }

  get currentPageNumber(): number {
    return Math.floor(this.from / this.pageSize) + 1;
  }

  private showPage(page: number): void {
    this.from = (page - 1) * this.pageSize;
    this.to = this.from + this.pageSize;
    this.clampPage();
  }

  private clampPage(): void {
    if (!this.itemsSource) return;
    const lastPage = Math.max(1, Math.ceil(this.itemsSource.length / this.pageSize));
    if (this.currentPageNumber > lastPage) {
      this.from = (lastPage - 1) * this.pageSize;
      this.navigation.setListPage(lastPage);
    }
    this.to = this.from + this.pageSize;
  }

  execute(commandId: string, contextId?: string) {
    this.showMenu = false;
    let items = this.getSelectedItems();
    if (items.length > 0) {
      if (this.externalMenuCommandIds?.includes(commandId)) {
        const command =
          this.control.value && this.selectedItemsCount === this.getSelectableRows().length
            ? this._commandFactory.createFilteredCommand(commandId, [], [], this.selectedItemsCount)
            : this._commandFactory.createMultiselectCommand(commandId, items, this.itemsSource, contextId);
        this.menuCommand.emit(command);
      } else {
        const command = this._commandFactory.createMultiselectCommand(commandId, items, this.itemsSource, contextId);
        this._commandDispatcher.sendCommand(command);
      }
    } else {
      this._messageService.showWarning('WARNING.NO_SELECTED_ITEMS');
    }
  }

  ngDoCheck() {
    super.ngDoCheck();
    let selection = this.getSelectedItems();
    const selectableRows = this.getSelectableRows();
    this.selectedItemsCount = selection.length;
    this.isActive = this.selectedItemsCount > 0;
    if (this.selectedItemsCount == 0) {
      this.showMenu = false;
    }
    this.control.setValue(selectableRows.length > 0 && this.selectedItemsCount === selectableRows.length, { emitEvent: false });
    this._cdr.detectChanges();
  }

  ngOnChanges(changes: SimpleChanges): void {
    super.ngOnChanges(changes);
    // An empty result after loading has no iterable changes, but must still clamp pagination.
    if (changes['itemsSource'] && this.rememberPagination) this.clampPage();
    if (changes['selectAll']) {
      this.control.setValue(this.selectAll);
    }
  }

  private cancelHide = false;

  onMenuMouseEnter(): void {
    this.showMenu = true;
    this.cancelHide = true;
  }

  onMenuMouseLeave(): void {
    this.cancelHide = false;
    setTimeout(() => {
      if (!this.cancelHide) {
        this.showMenu = false;
      }
    }, 200);
  }

  openMenu() {
    if (this.isActive) this.showMenu = !this.showMenu;
  }

  pagingChanged(e: PagingEvent) {
    const pageSizeChanged = e.itemsPerPage !== this.pageSize;
    if (pageSizeChanged) this.pageSize = e.itemsPerPage;
    this.from = e.from;
    this.to = e.to;
    if (this.rememberPagination) {
      this.navigation.setListPage(e.pageNumber);
      if (pageSizeChanged && this.preferencesId !== undefined) {
        this.paginationSubscriptions.add(this.userPreferences.setListPageSize(this.preferencesId, e.itemsPerPage).subscribe());
      }
    }
  }

  isSelectionDisabled(row: any): boolean {
    return !!(row?.disableSelection || row?.dataSource?.disableSelection);
  }

  onRowSelectionChanged(row: { selected: boolean }, selected: boolean): void {
    row.selected = selected;
    this.emitSelectedItemsWhenChanged(this.getSelectedItems());
  }

  private getSelectableRows() {
    return (this.rows || []).filter(row => !this.isSelectionDisabled(row));
  }

  private scheduleSelectedItemsReconciliation(): void {
    if (this.selectionReconciliationScheduled || !this.hasSelectedItemsChanged(this.getSelectedItems())) return;

    this.selectionReconciliationScheduled = true;
    queueMicrotask(() => {
      this.selectionReconciliationScheduled = false;
      this.emitSelectedItemsWhenChanged(this.getSelectedItems());
    });
  }

  private emitSelectedItemsWhenChanged(selection: TSource[]): void {
    if (!this.hasSelectedItemsChanged(selection)) return;

    this.previouslySelectedItems = selection;
    this.selectedItemsChange.emit(selection);
  }

  private hasSelectedItemsChanged(selection: TSource[]): boolean {
    return selection.length !== this.previouslySelectedItems.length
      || selection.some((item, index) => item !== this.previouslySelectedItems[index]);
  }
}
